import React, { useState, Component } from 'react';
import { KpiCard } from '../KpiCard';
import { Plug, CheckCircle2, Settings, ExternalLink, AlertCircle, Zap, RefreshCw } from 'lucide-react';
const CARRIER_LOGOS: Record<string, string> = {
  BRT: 'https://cdn.magicpatterns.com/uploads/cmtJEf49a5eVZLpdSNFSih/BRT_logo_cropped.png',
  FedEx: 'https://cdn.magicpatterns.com/uploads/bJFTTvq6RiT6tx6MWd19DM/FedEx-Logo.png',
  GLS: 'https://cdn.magicpatterns.com/uploads/kLsfczSQhw6HQ17cu2iXe1/GLS_Logo_2021.svg.png',
  SDA: 'https://cdn.magicpatterns.com/uploads/uzuZLxLhuWymtodu3YDZcY/1620120317-sda-poste-logo.webp',
  UPS: 'https://cdn.magicpatterns.com/uploads/5fMcthEgdAwTbqPzFRcjsj/logo_corriere_ups_500.png',
  DHL: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/ac/DHL_Logo.svg/2560px-DHL_Logo.svg.png',
  Poste: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d8/Poste_italiane_logo_2018.svg/2560px-Poste_italiane_logo_2018.svg.png'
};
type IntegrationStatus = 'Connesso' | 'Disconnesso' | 'Errore' | 'In setup';
type Integration = {
  carrier: string;
  description: string;
  status: IntegrationStatus;
  lastSync?: string;
  apiVersion?: string;
  features: string[];
};
const INITIAL_INTEGRATIONS: Integration[] = [{
  carrier: 'FedEx',
  description: 'API ufficiale FedEx Web Services per tracking, etichette e quotazioni real-time.',
  status: 'Connesso',
  lastSync: '2 min fa',
  apiVersion: 'v2.4',
  features: ['Tracking', 'Etichette', 'Quotazioni', 'Resi']
}, {
  carrier: 'GLS',
  description: 'Integrazione GLS Italy per gestione spedizioni nazionali e internazionali EU.',
  status: 'Connesso',
  lastSync: '5 min fa',
  apiVersion: 'v3.1',
  features: ['Tracking', 'Etichette', 'Quotazioni']
}, {
  carrier: 'UPS',
  description: 'API UPS REST per spedizioni express e standard.',
  status: 'Connesso',
  lastSync: '8 min fa',
  apiVersion: 'v2.0',
  features: ['Tracking', 'Etichette', 'Quotazioni', 'Resi']
}, {
  carrier: 'BRT',
  description: 'Web service SOAP BRT per gestione spedizioni in Sicilia e Sud Italia.',
  status: 'Errore',
  lastSync: '2 ore fa',
  apiVersion: 'v1.8',
  features: ['Tracking', 'Etichette']
}, {
  carrier: 'SDA',
  description: 'Integrazione SDA Poste per consegne capillari in tutta Italia.',
  status: 'In setup',
  apiVersion: 'v2.2',
  features: ['Tracking', 'Etichette', 'Quotazioni']
}, {
  carrier: 'DHL',
  description: 'DHL Express API per spedizioni internazionali premium.',
  status: 'Disconnesso',
  apiVersion: 'v3.0',
  features: ['Tracking', 'Etichette', 'Quotazioni', 'Resi']
}, {
  carrier: 'Poste',
  description: 'Poste Italiane per raccomandate e pacchi assicurati.',
  status: 'Disconnesso',
  apiVersion: 'v1.5',
  features: ['Tracking', 'Etichette']
}];
const STATUS_CONFIG: Record<IntegrationStatus, {
  bg: string;
  text: string;
  border: string;
  icon: ComponentType<any>;
  dot: string;
}> = {
  Connesso: {
    bg: 'bg-[#D4FF3A]/10',
    text: 'text-[#D4FF3A]',
    border: 'border-[#D4FF3A]/30',
    icon: CheckCircle2,
    dot: 'bg-[#D4FF3A]'
  },
  Disconnesso: {
    bg: 'bg-[#1F1F1F]',
    text: 'text-[#9A9A9A]',
    border: 'border-[#262626]',
    icon: Plug,
    dot: 'bg-[#6B6B6B]'
  },
  Errore: {
    bg: 'bg-[#EF4444]/10',
    text: 'text-[#EF4444]',
    border: 'border-[#EF4444]/30',
    icon: AlertCircle,
    dot: 'bg-[#EF4444]'
  },
  'In setup': {
    bg: 'bg-[#F5A524]/10',
    text: 'text-[#F5A524]',
    border: 'border-[#F5A524]/30',
    icon: RefreshCw,
    dot: 'bg-[#F5A524]'
  }
};
export function Integrazioni() {
  const [integrations, setIntegrations] = useState<Integration[]>(INITIAL_INTEGRATIONS);
  const connectedCount = integrations.filter(i => i.status === 'Connesso').length;
  const errorCount = integrations.filter(i => i.status === 'Errore').length;
  const toggleConnection = (carrier: string) => {
    setIntegrations(prev => prev.map(i => i.carrier === carrier ? {
      ...i,
      status: i.status === 'Connesso' ? 'Disconnesso' : 'Connesso',
      lastSync: i.status === 'Connesso' ? undefined : 'ora'
    } : i));
  };
  return <div className="space-y-6" data-id="element-2025">
      <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-2026">
        Integrazioni
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-2027">
        {integrations.map(it => {
        const cfg = STATUS_CONFIG[it.status];
        return <div key={it.carrier} className="bg-[#181818] border border-[#262626] rounded-xl p-4 flex items-center gap-3" data-id="element-2028">
              <div className="h-10 w-14 bg-white rounded p-1.5 flex items-center justify-center shrink-0 border border-[#262626]" data-id="element-2029">
                <img src={CARRIER_LOGOS[it.carrier]} alt={it.carrier} className="max-w-full max-h-full object-contain" data-id="element-2030" />
              </div>

              <div className="flex-1 min-w-0" data-id="element-2031">
                <div className="text-sm font-semibold text-[#FAFAFA] font-heading truncate" data-id="element-2032">
                  {it.carrier}
                </div>
                <div className="flex items-center gap-1.5 mt-0.5" data-id="element-2033">
                  <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} data-id="element-2034" />
                  <span className={`text-[11px] font-medium ${cfg.text}`} data-id="element-2035">
                    {it.status}
                  </span>
                </div>
              </div>
            </div>;
      })}
      </div>
    </div>;
}
function RollupCard({
  label,
  value,
  accent
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return <div className="bg-[#181818] border border-[#262626] rounded-xl p-4" data-id="element-2036">
      <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-2037">
        {label}
      </div>
      <div className={`text-2xl font-mono font-semibold mt-1 ${accent ? 'text-[#F5A524]' : 'text-[#FAFAFA]'}`} data-id="element-2038">
        {value}
      </div>
    </div>;
}